import { usePublicServerInfo } from '@go-client/context/PublicServerInfoContext';
import { groupBy, mapBy } from '@go-shared/lib/functional';
import {
  buildCalendarMarkers,
  buildDateOption,
  buildReserveSlotKey,
  buildTicketSelections,
  DEFAULT_RENTAL_KEY,
  experienceIsRental,
  getSlotSpaceStatus,
  buildReserveTimeslotLabel,
  buildDayspanSubsequentSlots,
  timeSorter,
} from '@go-shared/lib/reserve';
import {
  addMinutesToTime,
  calculateTimeDifference,
  dateToIsoUTC,
  dateToIsoLocal,
} from '@go-shared/lib/time';
import { makeFullName } from '@go-shared/lib/utils';
import {
  ReservableEventTemplateSnapshot,
  ReservableEventWithTimeslots,
} from '@go-shared/types/reservable';
import { ReserveScheduleSlotPriceOverrides } from '@go-shared/types/reserveSchedule';
import { SimpleUser } from '@go-shared/types/user';
import React, { useMemo, useState } from 'react';
import {
  BubbleAlert,
  BubbleButton,
  BubbleCalendar,
  BubbleCheckbox,
  BubbleFormGroup,
  BubbleHintSelect,
  BubbleInput,
  BubbleOverlay,
} from './BubbleUI';
import {
  dayspanSlotToFormikOccurrence,
  ReservationFormikOccurrence,
} from './Formik/ReservationFormik';

export interface OccurrenceFillerProps {
  values: ReservationFormikOccurrence;
  onChange?: (
    next: ReservationFormikOccurrence,
    priceOverrides?: ReserveScheduleSlotPriceOverrides
  ) => void;
  onSetAdditionalOccurrences?: (
    additionalOccurrences: ReservationFormikOccurrence[],
    next: ReservationFormikOccurrence,
    priceOverrides?: ReserveScheduleSlotPriceOverrides
  ) => void;
  onClear?: () => void;
  ticketQuantities: Array<{ ticket: { _id: string }; quantity: number }>;
  users: SimpleUser[];
  eventSnapshot: ReservableEventTemplateSnapshot;
  reservableDetails?: ReservableEventWithTimeslots;
  reservableDetailsLoading?: boolean;
  readOnly?: boolean;
  hideAdvancedMode?: boolean;
  showUnavailableTimeslots?: boolean;
}

export default function OccurrenceFiller(props: OccurrenceFillerProps) {
  const {
    values,
    onChange,
    onSetAdditionalOccurrences,
    ticketQuantities,
    users,
    eventSnapshot,
    reservableDetails,
    reservableDetailsLoading,
    readOnly,
    onClear,
    hideAdvancedMode,
    showUnavailableTimeslots,
  } = props;

  const { serverDate } = usePublicServerInfo();
  const [advancedMode, setAdvancedMode] = useState(false);

  const rentalsList = eventSnapshot.reservables.tickets;
  const usersMap = mapBy(users, u => u._id);
  const rentalsMap = mapBy(rentalsList, r => r._id);
  const ticketsMap = mapBy(rentalsList, t => t._id);
  const isRental = experienceIsRental(eventSnapshot.reservables.experienceType);

  const sortedTimeslots = useMemo(() => {
    const timeslots = reservableDetails?.timeslots ?? [];
    const rentalRank = new Map<string, number>(rentalsList.map((t, i) => [t._id, i]));

    return [...timeslots].sort((a, b) => {
      // grouped by exact dayspan, ascending, ahead of date/rental: all 1-day slots, then all
      // 2-day, then all 3-day, etc. - timeSorter below also groups by dayspan, but only as its
      // first tiebreaker, which would rank it under date/rental if we relied on that alone
      const dayspanDiff = a.dayspan - b.dayspan;
      if (dayspanDiff !== 0) return dayspanDiff;

      const dateDiff = new Date(a.date).getTime() - new Date(b.date).getTime();
      if (dateDiff !== 0) return dateDiff;

      const rentalDiff =
        (rentalRank.get(a.rental ?? '') ?? -1) - (rentalRank.get(b.rental ?? '') ?? -1);
      if (rentalDiff !== 0) return rentalDiff;

      return timeSorter(a, b);
    });
  }, [reservableDetails, rentalsList]);

  const timeslotsByDate = groupBy(sortedTimeslots, t => dateToIsoUTC(t.date));

  const selectedDate = buildDateOption(
    values.date
      ? values.date
      : sortedTimeslots[0]?.date
      ? dateToIsoUTC(sortedTimeslots[0]?.date)
      : serverDate.iso,
    timeslotsByDate
  );

  const selectedSlotKey = hideAdvancedMode
    ? undefined
    : values.time
    ? buildReserveSlotKey(
        { date: values.date, rental: values.rental, host: values.host },
        values.time,
        values.duration
      )
    : undefined;

  const selectedDateTimesByRental = groupBy(
    timeslotsByDate[selectedDate.key] ?? [],
    t => t.rental || DEFAULT_RENTAL_KEY
  );

  const displayedDateTimesByRental = Object.fromEntries(
    Object.entries(selectedDateTimesByRental)
      .map(([rental, times]) => {
        const filteredTimes = showUnavailableTimeslots
          ? times
          : times.filter(item => {
              const { dynamicSpotsLeft } = getSlotSpaceStatus(item);
              // we show ourselves even if we're on no capacity regardless
              return dynamicSpotsLeft > 0 || selectedSlotKey === item.slotKey;
            });

        return [rental, filteredTimes] as const;
      })
      .filter(([, times]) => times.length > 0)
  );

  const allRentalTimes = Object.entries(displayedDateTimesByRental);

  return (
    <div className="grid grid-cols-1 gap-4">
      <div className="flex items-start">
        <BubbleFormGroup label="Date" className="flex-shrink-0 pr-4" style={{ width: 450 }}>
          <BubbleOverlay loading={reservableDetailsLoading}>
            <BubbleCalendar
              responsive
              readOnly={readOnly}
              selectedDate={selectedDate.date}
              onDateSelect={date => onChange?.({ ...values, date: dateToIsoLocal(date) })}
              addMarkers={dayContext =>
                buildCalendarMarkers(
                  dayContext.dateIso,
                  timeslotsByDate,
                  buildTicketSelections(eventSnapshot.reservables.tickets, ticketQuantities)
                    .quantities,
                  values.time
                    ? { time: values.time, duration: values.duration, host: values.host }
                    : undefined,
                  values.rental
                )
              }
              showToday
              showClear={!!onClear}
              onClear={onClear}
              subtitle={() => (
                <div className="text-xs text-secondary">
                  <div className="flex items-center gap-1">
                    <span
                      className="inline-block rounded-full"
                      style={{ width: 8, height: 8, backgroundColor: '#22c55e' }}
                    />
                    Available for your tickets and timeslot
                  </div>
                  <div className="flex items-center gap-1">
                    <span
                      className="inline-block rounded-full"
                      style={{ width: 8, height: 8, backgroundColor: '#f59e0b' }}
                    />
                    Available, but not for your tickets or timeslot
                  </div>
                  <div className="flex items-center gap-1">
                    <span
                      className="inline-block rounded-full"
                      style={{ width: 8, height: 8, backgroundColor: '#ef4444' }}
                    />
                    No availability for this date
                  </div>
                </div>
              )}
            />
          </BubbleOverlay>
        </BubbleFormGroup>
        <div style={{ width: 'calc(100% - 450px)' }} className="flex-shrink-0 flex flex-col gap-4">
          {allRentalTimes.length > 0 ? (
            <div className="overflow-x-auto p-1 -m-1">
              <div className="flex gap-4">
                {allRentalTimes.map(([rental, times]) => (
                  <BubbleFormGroup
                    key={rental}
                    label={
                      isRental ? ticketsMap[rental]?.name ?? 'Unassigned Rental' : 'Availability'
                    }
                    className="flex-shrink-0"
                    style={{ width: 200 }}
                  >
                    <div className="flex flex-col gap-2">
                      {times.map(item => {
                        const isSelected = selectedSlotKey === item.slotKey;
                        const timeLabel = buildReserveTimeslotLabel({
                          time: item.time,
                          timeDisplay: item.timeDisplay,
                          duration: item.duration,
                          dayspan: item.dayspan,
                        });
                        const hostLabel = item.host
                          ? makeFullName(
                              usersMap[item.host]?.firstName,
                              usersMap[item.host]?.lastName
                            )
                          : undefined;

                        const { dynamicCapacity, dynamicSpotsLeft } = getSlotSpaceStatus(item);

                        const selectedVariant = dynamicSpotsLeft > 0 ? 'navy' : 'smoke';
                        const selectedTextVariant =
                          dynamicSpotsLeft > 0 ? '' : isSelected ? 'white' : 'smoke';
                        const priceOverrideKeys = Object.keys(item.priceOverrides || {});

                        return (
                          <BubbleButton
                            key={item._id}
                            block
                            className={`border border-${selectedVariant} ${
                              dynamicSpotsLeft < 1 ? 'italic' : ''
                            }`}
                            variant={isSelected ? selectedVariant : 'white'}
                            onClick={() => {
                              const spanSlots = buildDayspanSubsequentSlots(item);

                              const newVals = {
                                ...values,
                                date: dateToIsoUTC(item.date),
                                time: item.time,
                                timeDisplay: item.timeDisplay,
                                duration: item.duration,
                                host: item.host,
                                rental: item.rental,
                              };

                              if (onSetAdditionalOccurrences) {
                                onSetAdditionalOccurrences(
                                  spanSlots.map(dayspanSlotToFormikOccurrence),
                                  newVals,
                                  item.priceOverrides || {}
                                );
                              } else {
                                onChange?.(newVals, item.priceOverrides || {});
                              }
                            }}
                          >
                            <div className="flex items-center whitespace-normal">
                              <div className="text-left mr-3">
                                {item.label && (
                                  <div className={`text-${selectedTextVariant}`}>{item.label}</div>
                                )}
                                <div
                                  className={`${
                                    dynamicSpotsLeft === 0 ? `text-${selectedTextVariant}` : ''
                                  } ${
                                    item.label
                                      ? `font-normal text-sm ${
                                          isSelected
                                            ? 'text-eggshell'
                                            : dynamicSpotsLeft !== 0
                                            ? 'text-slate'
                                            : ''
                                        }`
                                      : ''
                                  }`}
                                >
                                  {timeLabel}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center whitespace-normal">
                              {hostLabel && (
                                <div
                                  className={`text-sm ${
                                    isSelected ? 'text-eggshell' : 'text-slate'
                                  }`}
                                >
                                  {hostLabel}
                                </div>
                              )}
                              <div
                                className={`ml-auto mt-1 items-center text-${selectedTextVariant}`}
                              >
                                {dynamicCapacity - dynamicSpotsLeft} / {dynamicCapacity}
                              </div>
                            </div>
                            <div className="flex items-center whitespace-normal">
                              {priceOverrideKeys.length > 0 && (
                                <div
                                  className={`text-sm mt-1 ${
                                    isSelected ? 'text-eggshell' : 'text-slate'
                                  }`}
                                >
                                  + price overrides
                                </div>
                              )}
                            </div>
                          </BubbleButton>
                        );
                      })}
                    </div>
                  </BubbleFormGroup>
                ))}
              </div>
            </div>
          ) : (
            <BubbleFormGroup label="Time Slots">
              <BubbleAlert variant="banana">No timeslots available to show.</BubbleAlert>
            </BubbleFormGroup>
          )}
        </div>
      </div>
      {!hideAdvancedMode && (
        <div>
          <BubbleCheckbox
            label="Edit Manually (Custom Timeslot)"
            checked={advancedMode}
            onValue={setAdvancedMode}
            subtitle="Allows you to set a custom date, time, host, and rental. Use with caution, as this can create reservations that don't align with available timeslots. Unticking this will not reset any of the custom values you've entered."
          />
          {advancedMode && (
            <fieldset className="grid grid-cols-6 gap-4 mt-4">
              <BubbleFormGroup label="Date" className="col-span-2">
                <BubbleInput
                  block
                  type="date"
                  readOnly={readOnly}
                  value={values.date}
                  onValue={val => onChange?.({ ...values, date: val })}
                />
              </BubbleFormGroup>
              <BubbleFormGroup label="Start Time" className="col-span-2">
                <BubbleInput
                  block
                  type="time"
                  readOnly={readOnly}
                  value={values.time}
                  onValue={val => onChange?.({ ...values, time: val })}
                />
              </BubbleFormGroup>
              <BubbleFormGroup label="End Time" className="col-span-2">
                <BubbleInput
                  type="time"
                  block
                  readOnly={readOnly}
                  value={
                    !values.time
                      ? undefined
                      : values.duration
                      ? addMinutesToTime(values.time, values.duration)
                      : values.time
                  }
                  onValue={val => {
                    if (!val || !values.time) return;
                    onChange?.({ ...values, duration: calculateTimeDifference(val, values.time) });
                  }}
                />
              </BubbleFormGroup>
              <BubbleFormGroup label="Time Display" className="col-span-2">
                <BubbleInput
                  block
                  type="text"
                  readOnly={readOnly}
                  value={values.timeDisplay}
                  onValue={val => onChange?.({ ...values, timeDisplay: val })}
                />
              </BubbleFormGroup>
              <BubbleFormGroup label="Host" className="col-span-2">
                <BubbleHintSelect
                  block
                  allowUndefined
                  options={users.map(u => u._id)}
                  labelExtractor={u =>
                    makeFullName(usersMap[u]?.firstName, usersMap[u]?.lastName) || 'Unknown User'
                  }
                  readOnly={readOnly}
                  value={values.host}
                  onValue={val => onChange?.({ ...values, host: val })}
                />
              </BubbleFormGroup>
              <BubbleFormGroup label="Rental" className="col-span-2">
                <BubbleHintSelect
                  block
                  allowUndefined
                  options={rentalsList.map(u => u._id)}
                  labelExtractor={u => rentalsMap[u]?.name || 'Unassigned Rental'}
                  readOnly={readOnly}
                  value={values.rental}
                  onValue={val => onChange?.({ ...values, rental: val })}
                />
              </BubbleFormGroup>
            </fieldset>
          )}
        </div>
      )}
    </div>
  );
}
